/* LEGAL-NOTICE
   © Copyright 2018 - Quid Informatica S.p.A. - All Rights Reserved
   https://quidinfo.it/qinetic/licence.html - "Quid Consumer Lending Platform"
   Deposito SIAE n. 012702 del 26 Luglio 2018
LEGAL-NOTICE */
import { QuotationDTO } from '@api/models/.';
import { QuotationDataModel } from '@models/.';
import {
  Component,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  EventEmitter
} from '@angular/core';
import { QuotationService } from '@services/quotation/quotation.service';
import { UiService } from '@services/ui/ui.service';
import { AnalyticsService } from '@services/analytics/analytics.service';
import { LabelsService } from '@core/services/labels/labels.service';
import { tap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';

@Component({
  selector: 'app-quotation-preview',
  templateUrl: './quotation-preview.component.html',
  styleUrls: ['./quotation-preview.component.scss'],
  standalone: false
})
export class QuotationPreviewComponent implements OnChanges {
  /**
   * nome cliente
   */
  @Input() customerName: string;

  /**
   * dati quotation
   */
  @Input() quotation: QuotationDTO;

  /**
   * dati primari visualizzati nella card
   */
  @Input() primaryData: QuotationDataModel[];

  /**
   * flag per visualizzare il bottone modifica
   */
  @Input() editable: boolean;

  /**
   * lista di codici che indica il motivo per il quale il preventivo risulta aggiornato
   * rispetto a quanto richiesto dall'utente
   */
  @Input() updatedAfter: string[];

  /**
   * evento emesso sul click del pulsante di modifica
   */
  @Output() edit = new EventEmitter<void>();

  /**
   * dettagli
   */
  detailsText: string;

  /**
   * dettagli per screen reader
   */
  ariaDetailsText: string;

  /**
   * motivazione ricalcolo
   */
  updatedAfterText: string;

  /**
   * giorni in cui è possibile pagare
   */
  paymentDays: string[] = [];

  /**
   * flag che indica se è attiva la possibilità di cpi
   */
  hasCpiActive: boolean;

  /**
   * flag che indica se è presente cpi sul preventio
   */
  hasCpiInlcuded: boolean;

  /**
   * flag che indica se è attiva la possibilità di pre ammortamento
   */
  hasPreAmortizationActive: boolean;

  /**
   * flag che indica se è incluso pre ammortamento sul preventivo
   */
  hasPreAmortizationIncluded: boolean;

  constructor(
    private uiService: UiService,
    private quotationService: QuotationService,
    private analyticsService: AnalyticsService,
    private labelsService: LabelsService
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (!!changes.quotation || !!changes.primaryData) {
      this.updateDetailsText();
      this.updateRelatedData();
      this.mapData();
      this.updateAnalyticsMetadata();
    }

    if (!!changes.quotation || !!changes.updatedAfter) {
      this.updateUpdatedAfterText();
    }
  }

  onEdit(): void {
    this.edit.emit();
  }

  /**
   * apre modale di dettaglio
   */
  async openDetailsModal(): Promise<void> {
    const parts = [`lbl.quotation_details_message`];

    if (this.updatedAfter && this.updatedAfter.length > 0) {
      this.updatedAfter
        .map(code => `lbl.quotation_updated_after_${code}_message`)
        .forEach(label => parts.unshift(label));
    }

    if (
      !!this.quotation.insurances &&
      !!this.quotation.insurances.some(x => x.type === 'BP') &&
      !!this.quotation.boostInfos
    ) {
      if (
        !!this.quotation.boostInfos.rataCap &&
        !!this.quotation.boostInfos.rataFloor &&
        !!this.quotation.boostInfos.plafond
      ) {
        parts.push('lbl.quotation_flexi_options_jump_and_change_message');
      } else if (
        !!this.quotation.boostInfos.rataCap &&
        !!this.quotation.boostInfos.rataFloor
      ) {
        parts.push('lbl.quotation_flexi_options_change_message');
      } else if (!!this.quotation.boostInfos.plafond) {
        parts.push('lbl.quotation_flexi_options_jump_message');
      }
    }

    const description = parts
      .map(part =>
        this.quotationService.getLabelWithQuotationData(part, this.quotation)
      )
      .join('<br><br>');

    this.uiService.showTooltipAsModal({
      title: 'lbl.quotation_details_title',
      description
    });
  }

  /**
   * apre tooltip
   */
  openPreamortizationTooltip(): void {
    const duration = String(this.quotation.firstInstallmentDays);
    const paymentDays = this.paymentDays.join(', ');

    this.uiService.showTooltipAsModal({
      title: 'lbl.quotation_edit_pre_amortization_tooltip_title',
      description: this.labelsService.getLabel(
        'lbl.quotation_edit_pre_amortization_tooltip_description',
        {
          interpolateParams: {
            duration,
            paymentDays
          }
        }
      )
    });
  }

  /**
   * aggiorna i dettagli
   */
  private updateDetailsText(): void {
    this.detailsText = null;
    this.ariaDetailsText = null;

    this.loadPaymentDays().subscribe(() => {
      this.detailsText = this.quotationService.getLabelWithQuotationData(
        'lbl.quotation_confirm_details_message',
        this.quotation,
        { paymentDays: this.paymentDays }
      );

      this.ariaDetailsText = this.quotationService.getLabelWithQuotationData(
        'aria.quotation_confirm_details_message',
        this.quotation,
        { paymentDays: this.paymentDays }
      );
    });
  }

  /**
   * aggiorna dati collegati al preventivo
   */
  private updateRelatedData(): void {
    this.hasCpiInlcuded =
      !!this.quotation.insurances &&
      !!this.quotation.insurances.find(x => x.type === 'CPI');

    this.hasCpiActive = false;

    this.quotationService.getAvailableCPI().subscribe(code => {
      this.hasCpiActive = !!code;
    });

    this.hasPreAmortizationActive =
      this.quotation.minPreAmortization >= 0 &&
      this.quotation.maxPreAmortization > this.quotation.minPreAmortization;

    this.hasPreAmortizationIncluded =
      this.hasPreAmortizationActive && this.quotation.preAmortizationMonths > 0;
  }

  /**
   * aggiorna testo relativo alla motivazione di ricalcolo
   */
  private updateUpdatedAfterText(): void {
    if (this.updatedAfter.length > 0) {
      const key =
        this.updatedAfter.length === 1
          ? 'lbl.quotation_updated_after_' + this.updatedAfter[0]
          : 'lbl.quotation_updated_after_many';

      this.updatedAfterText = this.quotationService.getLabelWithQuotationData(
        key,
        this.quotation
      );
    } else {
      this.updatedAfterText = null;
    }
  }

  /**
   * mappa i dati della quotation
   */
  private mapData(): void {
    this.mapPrimaryData();
  }

  /**
   * mappa i dati della quotation sulla lista di primary data
   */
  private mapPrimaryData(): void {
    this.primaryData.forEach(
      data => (data.value = this.getQuotationFieldValue(data.id))
    );
  }

  /**
   * ottiene il value del dato recuperandolo dalla quotation
   */
  private getQuotationFieldValue(fieldId): number {
    const value = this.quotation && this.quotation[fieldId];

    if (typeof value === 'number') {
      return value;
    } else {
      return 0;
    }
  }

  /**
   * aggiorna i metadata per gli analytics
   */
  private updateAnalyticsMetadata(): void {
    this.analyticsService.setMetadata({
      quotationAmount: this.quotation?.disbursedAmount,
      quotationInstallment: this.quotation?.installment,
      quotationDuration: this.quotation?.duration,
      quotationTan: this.quotation?.tan,
      quotationTaeg: this.quotation?.taeg,
      totalCostOfCredit: this.quotation?.totalCostOfCredit
    });
  }

  private loadPaymentDays(): Observable<any> {
    return (
      !this.quotation
        ? of([])
        : this.quotationService.getPaymentDaysOptions(this.quotation)
    ).pipe(
      tap(res => {
        this.paymentDays = res.map(x => x.value);
      })
    );
  }
}
